/**
 * Detects whether the current browser exposes a platform passkey
 * authenticator (Touch ID, Windows Hello, Android Keystore, etc.) that
 * supports user-verifying WebAuthn ceremonies.
 *
 * Used by every route on mount to render a friendly fallback message
 * when the browser is too old or runs in a context (insecure origin,
 * incognito modes that disable WebAuthn) where the ceremony cannot
 * succeed. Cheaper than waiting for the ceremony itself to error out.
 *
 * @returns true when the browser will accept a WebAuthn request with
 *          userVerification: 'required'; false otherwise.
 */
export async function probe_passkey_support(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const pkc = (
    window as {
      PublicKeyCredential?: {
        isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
      };
    }
  ).PublicKeyCredential;
  if (
    pkc === undefined ||
    typeof pkc.isUserVerifyingPlatformAuthenticatorAvailable !== 'function'
  ) {
    return false;
  }
  try {
    const available = await pkc.isUserVerifyingPlatformAuthenticatorAvailable();
    return available === true;
  } catch {
    return false;
  }
}
